import { Capacitor } from '@capacitor/core';
import { Box } from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { captureEvent } from '../../../services/event-capture';
import { ROUTE_MAP_INFORMATION } from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import {
  selectAppViewportSize,
  updateViewportSize,
} from '../../../services/store/modules/app-controls';
import { selectCurrentMember } from '../../../services/store/modules/backend-service';
import BottomControlPane from '../../shared/MapControls/BottomControlPane';
import RightControlColumn from '../../shared/MapControls/RightControlColumn';
import TopControlPane from '../../shared/MapControls/TopControlPane';
import TopLeftControlPane from '../../shared/MapControls/TopLeftControlPane';
import StatusBar from '../../shared/StatusBar/StatusBar';
import { infoCircleFlush, ReactSvg } from '../../shared/vector-images';
import styles from './AppControls.scss';
import ConnectedActiveTripButton from './ConnectedActiveTripButton';
import ConnectedControlBar from './ConnectedControlBar';
import ConnectedMenuBar from './ConnectedMenuBar';
import ConnectedOverlay from './ConnectedOverlay';
import ConnectedSelectMapPoint from './ConnectedSelectMapPoint';
import ConnectedSharingDialog from './ConnectedSharingDialog';
import DropPinTargetOverlay from '../../features/DropPin/DropPinTargetOverlay';
import TripDebugPanel from './TripDebugPanel';

const cx = classNames.bind(styles);

const StyledTopControlPane = styled(TopControlPane)`
  pointer-events: all;
`;

const StyledTopLeftControlPane = styled(TopLeftControlPane)`
  z-index: 100;
  pointer-events: all;
  position: absolute;
  top: 10px;
  left: 10px;
`;

const StyledBottomControlPane = styled(BottomControlPane)`
  pointer-events: all;
`;

const MapInfoButton = ({ className }) => (
  <Box
    as="button"
    className={className}
    onClick={() => {
      captureEvent({
        type: 'app-controls: click map information button',
        payload: {},
        sendToAmplitude: true,
      });

      transitionToNextRoute(ROUTE_MAP_INFORMATION);
    }}
  >
    <ReactSvg
      toDisplay={infoCircleFlush()}
      width={24}
      height={24}
      options={{ fillColor: '#ffffff80' }}
    />
  </Box>
);

const StyledMapInfoButton = styled(MapInfoButton)`
  /* Button reset */
  all: unset;
  outline: revert;

  cursor: pointer;
  z-index: 100;
  pointer-events: all;
  position: absolute;

  left: 10px;
  bottom: ${({ theme }) =>
    `calc(${theme.layout.controlBarHeight} + ${theme.space[3]}px)`};

  ${({ theme }) =>
    `${theme.mediaQueries.medium} {
bottom: ${theme.space[3]}px;
}`}
`;

const StyledConnectedActiveTripButton = styled(ConnectedActiveTripButton)`
  pointer-events: all;
  position: absolute;
  left: 10px;
  z-index: 10;
  bottom: ${({ theme }) =>
    `calc(${theme.layout.controlBarHeight} + ${theme.space[3]}px)`};

  ${({ theme }) =>
    `${theme.mediaQueries.medium} {
      bottom: ${theme.space[3]}px;
    }`}
`;

const AppControls = ({ className }) => {
  const dispatch = useDispatch();
  const appViewportSize = useSelector(selectAppViewportSize);
  const member = useSelector(selectCurrentMember);

  // For now the top left control pane is specifically for freedom boat club. Hard coding the logic here purely for time, however
  // this is designed in a way which replicates the functionality of the right hand side control pane.
  const enableFreedomBoatClub = [
    'David.Kurczewski@brunswick.com',
    'Philip.Greene@boateka.com',
    'pgreeneiii@gmail.com',
    'jason@deckee.com',
  ]
    .map((email) => email.toLocaleLowerCase())
    .includes(member?.email.toLowerCase());

  const showingPopOver = useSelector(
    (state) => state.appControls.showingPopOver,
  );

  // eslint-disable-next-line no-warning-comments
  // TODO: Move handling the screen resize to somewhere it matters
  // i.e. why do we need to handle screen resizing? If it is for a
  // particular component to work properly, then have the resize functionality
  // on that component.
  const handleUpdateAppViewportSize = useCallback(() => {
    dispatch(updateViewportSize());
  }, [dispatch]);

  useEffect(() => {
    handleUpdateAppViewportSize();
    // eslint-disable-next-line no-warning-comments
    // TODO: Need to look into debouncing - but this can cause issues with app height
    window.addEventListener('resize', handleUpdateAppViewportSize);
  }, [handleUpdateAppViewportSize]);

  const viewClasses = cx('app-controls', {
    [className]: className,
  });

  return (
    <div
      id="appControlsHolder"
      className={viewClasses}
      style={{
        minHeight:
          appViewportSize.height > 0 ? `${appViewportSize.height}px` : '100vh',
        maxHeight:
          appViewportSize.height > 0 ? `${appViewportSize.height}px` : '100vh',
        height:
          appViewportSize.height > 0 ? `${appViewportSize.height}px` : '100vh',
      }}
    >
      <ConnectedSharingDialog />

      <ConnectedOverlay />

      <ConnectedMenuBar />

      <ConnectedControlBar />

      {/* Map controls */}
      <StyledTopLeftControlPane enableFreedomBoatClub={enableFreedomBoatClub} />

      <RightControlColumn>
        <StyledTopControlPane />
        <StyledBottomControlPane />
      </RightControlColumn>
      <StyledMapInfoButton />

      {Capacitor.isNativePlatform() && <StyledConnectedActiveTripButton />}

      <DropPinTargetOverlay />

      <ConnectedSelectMapPoint />

      <StatusBar show={showingPopOver} />

      <TripDebugPanel />
    </div>
  );
};

AppControls.propTypes = {
  className: PropTypes.string,
};

AppControls.defaultProps = {
  className: null,
};

export default AppControls;
